import { ContentfulCollection } from '@jbhi-fi/apis/src/contentful/types';
import { useQueries } from 'react-query';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { usePublicApiClient } from '../usePublicApiClient';

export type UseContentfulCollectionResult = { collection?: ContentfulCollection; isLoading: boolean; error?: unknown };

type UseContentfulCollectionProps = {
  handle: string;
  contentfulThemeApiClient?: ContentfulThemeApiClient;
  linkedEntryDepth?: number | string;
};

export const useContentfulCollections = ({
  collections,
  contentfulThemeApiClient: contentfulThemeApiClientProp,
}: {
  collections: Array<Omit<UseContentfulCollectionProps, 'contentfulThemeApiClient'>>;
  contentfulThemeApiClient?: ContentfulThemeApiClient;
}): Array<UseContentfulCollectionResult> => {
  const { contentfulThemeApiClient: contentfulThemeApiClientContext, contentfulClient } = usePublicApiClient();
  const contentfulThemeApiClient = contentfulThemeApiClientProp || contentfulThemeApiClientContext;
  if (!contentfulThemeApiClient)
    throw new Error('useContentfulCollection must be used with a contentfulThemeApiClient');
  const isPreview = window.Jbhifi?.isContentPreview || false;
  const results = useQueries(
    collections
      .map(({ handle, linkedEntryDepth = '' }) => ({
        enabled: Boolean(handle),
        queryKey: ['useContentfulCollection', handle, linkedEntryDepth],
        queryFn: () =>
          isPreview
            ? contentfulThemeApiClient?.getCollection({ handle: handle.split('_')[0], include: `${linkedEntryDepth}` })
            : contentfulClient?.getCollection({ handle: handle.split('_')[0], include: `${linkedEntryDepth}` }),
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        /* Cache for up to 30 minutes. */
        staleTime: 30 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
      }))
      .filter(query => query.enabled)
  );

  return results.map(res => ({
    isLoading: res.status === 'loading',
    collection: res.data?.data?.items?.[0],
    error: res.error,
  }));
};

export const useContentfulCollection = ({
  handle,
  linkedEntryDepth = '',
  contentfulThemeApiClient,
}: UseContentfulCollectionProps): UseContentfulCollectionResult => {
  const [collectionQuery] = useContentfulCollections({
    collections: [{ handle, linkedEntryDepth }],
    contentfulThemeApiClient,
  });

  return {
    isLoading: collectionQuery?.isLoading,
    collection: collectionQuery?.collection,
    error: collectionQuery?.error,
  };
};
